import type { AppNotificationItem } from "@nlc/shared-types";

import dayjs from "dayjs";
import { Archive, Mail, MailOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  useAppNotifications,
  useUnreadNotificationCounts,
  useUpdateAppNotification,
} from "@/hooks/useAppNotifications";
import {
  formatActionLink,
  getNotificationTitle,
  NOTIFICATION_CONTENT_MAX_LINES,
  NotificationContentLines,
} from "@/utils/notificationDisplay";

const TABS = [
  {
    countKey: "all" as const,
    label: "All",
    storeId: "all" as const,
    tag: undefined,
  },
  {
    countKey: "request" as const,
    label: "Requests",
    storeId: "request" as const,
    tag: "request" as const,
  },
  {
    countKey: "mentions" as const,
    label: "Mentions",
    storeId: "mentions" as const,
    tag: "mention" as const,
  },
  {
    countKey: "archived" as const,
    label: "Archived",
    storeId: "archived" as const,
    tag: undefined,
  },
] as const;

type TabId = (typeof TABS)[number]["storeId"];

function NotificationItem({
  notification,
  onPress,
}: {
  notification: AppNotificationItem;
  onPress: () => void;
}) {
  const { mutate: updateNotification } = useUpdateAppNotification();

  return (
    <button
      className="py-2 px-4 flex flex-row justify-between hover:bg-bg-tertiary cursor-pointer w-full"
      onClick={onPress}
    >
      <div className="text-start w-full">
        <p
          className={`${notification.isRead ? "text-text-tertiary" : "text-fg-brand-primary-alt"} text-base font-medium`}
        >
          {getNotificationTitle(notification.category, notification.tag)}
        </p>
        {notification.content && (
          <NotificationContentLines
            content={notification.content}
            maxLines={NOTIFICATION_CONTENT_MAX_LINES}
          />
        )}
      </div>
      <div className="ml-4 shrink-0">
        <p className="text-sm font-normal text-text-tertiary">
          {dayjs(notification.createdAt).fromNow()}
        </p>
        <div className="flex flex-row justify-end items-center">
          {notification.isRead ? (
            <MailOpen
              className="text-text-tertiary cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                updateNotification({
                  body: { isRead: false },
                  id: notification.id,
                });
              }}
              size={17}
            />
          ) : (
            <Mail
              className="text-fg-brand-primary-alt cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                updateNotification({
                  body: { isRead: true },
                  id: notification.id,
                });
              }}
              size={17}
            />
          )}
          <Archive
            className={`${notification.isArchived ? "text-fg-brand-primary-alt" : "text-text-tertiary cursor-pointer"} ml-1`}
            onClick={(e) => {
              e.stopPropagation();
              if (!notification.isArchived) {
                updateNotification({
                  body: { isArchived: true },
                  id: notification.id,
                });
              }
            }}
            size={17}
          />
        </div>
      </div>
    </button>
  );
}

export function InHouseNotificationFeed() {
  const [activeTab, setActiveTab] = useState<TabId>("all");
  const sentinelRef = useRef<HTMLDivElement>(null);

  const activeStore = TABS.find((tab) => tab.storeId === activeTab) ?? TABS[0];

  const { mutate: updateNotification } = useUpdateAppNotification();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useAppNotifications({
      isArchived: activeTab === "archived",
      tag: activeStore.tag,
    });
  const { data: unreadCounts } = useUnreadNotificationCounts();

  const notifications = data?.pages.flatMap((page) => page.notifications) ?? [];

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          fetchNextPage();
        }
      },
      { root: sentinel.closest("main"), rootMargin: "0px 0px 200px 0px" },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleNotificationPress = (notification: AppNotificationItem) => {
    if (!notification.isRead) {
      updateNotification({ body: { isRead: true }, id: notification.id });
    }

    if (notification.url) {
      window.open(formatActionLink(notification.url), "_blank");
    }
  };

  return (
    <div className="w-full bg-bg-primary">
      <div className="sticky top-0 z-[2] mb-[10px] flex overflow-x-auto bg-bg-primary [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((tab) => {
          const unreadCount = unreadCounts?.[tab.countKey] ?? 0;

          return (
            <button
              className={`flex cursor-pointer items-center gap-[5px] px-[15px] pb-[3px] pt-[5px] ${
                activeTab === tab.storeId
                  ? "rounded-sm bg-bg-brand-primary-alt"
                  : ""
              }`}
              key={tab.storeId}
              onClick={() => setActiveTab(tab.storeId)}
            >
              <span
                className={`text-sm font-semibold ${
                  activeTab === tab.storeId
                    ? "text-text-brand-secondary"
                    : "text-text-quaternary"
                }`}
              >
                {tab.label}
              </span>
              {unreadCount > 0 && (
                <span className="flex h-[18px] w-[20px] items-center justify-center rounded-full bg-[var(--color-text-brand-secondary)]">
                  <span
                    className={`font-semibold leading-none text-white ${
                      unreadCount > 99 ? "text-[8px]" : "text-[10px]"
                    }`}
                  >
                    {unreadCount}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <p className="mx-8 mt-10 text-center text-sm text-text-tertiary">
          Loading...
        </p>
      )}

      {!isLoading && notifications.length === 0 && (
        <div className="mx-8 mt-10 flex flex-col items-center justify-center">
          <p className="mb-4 mt-5 text-center text-base font-semibold text-text-primary">
            No notifications yet
          </p>
          <p className="text-center text-sm text-text-tertiary">
            We&apos;ll let you know when we&apos;ve got something new for you.
          </p>
        </div>
      )}

      {notifications.length > 0 && (
        <div>
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onPress={() => handleNotificationPress(notification)}
            />
          ))}
          {isFetchingNextPage && (
            <p className="py-4 text-center text-sm text-text-tertiary">
              Loading...
            </p>
          )}
          <div ref={sentinelRef} />
        </div>
      )}
    </div>
  );
}
